import React, {useDebugValue} from 'react';

import classNames from 'classnames';

import {useSearchFormContext} from '../../../hooks';

import {
    shouldDestinationFieldAutoProgressOnSearchSelected,
    showDestinationInputFieldError,
    showDestinationIcon
} from '../../../utils';

import {SearchIcon} from '../../SearchIcon';

const classNamespace = 'search-form';
const fieldClassName = `${classNamespace}__field`;
const inputFieldClassName = `${classNamespace}__input-field`;
const inputFieldErrorClassName = `${inputFieldClassName}--error`;
const detailsFieldClassName = `${classNamespace}__details-field`;
const detailsInputFieldClassName = `${classNamespace}__details-input-field`;
const destinationDetailsInputFieldClassName = `${classNamespace}__destination-details-input-field`;

// eslint-disable-next-line complexity
function useComponent() {
    const {
        system,
        system: {
            options: {
                site,
                baseUrl,
                appPrefix,
                locale
            }
        },
        media,
        media: {
            settings: {
                isMobile
            }
        },
        abacus,
        form,
        destination,
        destination: {
            actions: {
                setIsTypeaheadInputFocus,
                setAllValues
            },
            events: {
                onBlur,
                onFocus,
                onMount,
                onSearchSelected
            },
            options: {
                filterType,
                googlePlaceApiKey,
                googlePlaceApiUrl,
                hideSelectedFilter,
                highlightMode,
                iconName,
                id,
                matchMode,
                minLength,
                popoverDesktopMaxWidth,
                popoverDesktopMinMarginRight,
                popoverDesktopMinWidth,
                popoverDesktopWidth,
                popoverMaxDisplayCount,
                popoverMaxDisplayCountMobile,
                recentSearchesDisplayMax,
                remotePath,
                retainUserInput,
                showInlineSuggestion,
                showRecentSearches,
                showResultIcons,
                showResultLabels,
                showSpinner,
                showTrendingSuggestions,
                truncateResultTerms,
                usePopoverAnimation,
                useResultsAnimation
            },
            settings: {
                isTypeaheadInputFocus
            },
            translations: {
                clearSearchLabel,
                noResultsFoundMessage,
                placeholderMessage,
                premiseLabel,
                premisesLabel,
                recentSearchesMessage,
                recentlyViewedLabel,
                resultsFoundMessage,
                selectedMessage,
                trendingDestinationsMessage,
                typeAirportLabel,
                typeCityLabel,
                typeLandmarkLabel,
                typeMetroStationLabel,
                typeNeighborhoodLabel,
                typePropertyLabel,
                typeRegionLabel,
                typeTrainStationLabel,
                whereLabel
            },
            latestValues: {
                destination: search
            }
        },
        dates: {
            actions: {
                setIsOpen: setDatesIsOpen
            }
        },
        guests: {
            actions: {
                setIsOpen: setGuestsIsOpen
            }
        }
    } = useSearchFormContext();

    const className = classNames(fieldClassName, inputFieldClassName, {
        [inputFieldErrorClassName]: showDestinationInputFieldError(media, form, destination, abacus, system)
    }, detailsFieldClassName, detailsInputFieldClassName, destinationDetailsInputFieldClassName);

    const icon = showDestinationIcon(form, destination) && (
        <SearchIcon iconName={iconName}/>
    ) || null;

    const messages = {
        clearsearch: clearSearchLabel,
        destinationplaceholdermsg: placeholderMessage,
        destinationselected: selectedMessage,
        noresultsfound: noResultsFoundMessage,
        premise: premiseLabel,
        premises: premisesLabel,
        recentlyviewed: recentlyViewedLabel,
        recentsearches: recentSearchesMessage,
        resultsfound: resultsFoundMessage,
        trendingdestinations: trendingDestinationsMessage,
        typeairport: typeAirportLabel,
        typecity: typeCityLabel,
        typelandmark: typeLandmarkLabel,
        typemetrostation: typeMetroStationLabel,
        typeneighborhood: typeNeighborhoodLabel,
        typeproperty: typePropertyLabel,
        typeregion: typeRegionLabel,
        typetrainstation: typeTrainStationLabel,
        where: whereLabel
    };

    /* istanbul ignore next */
    function handleBlur(...args) {
        onBlur(...args);
    }

    function handleFocus(...args) {
        setIsTypeaheadInputFocus(true);
        setDatesIsOpen(false);
        setGuestsIsOpen(false);

        onFocus(...args);
    }

    function handleMount(...args) {
        onMount(...args);
    }

    function handleSearchSelected(...args) {
        const [
            changedValues
        ] = args;

        setAllValues(changedValues);

        // TODO: Move this function to handleBlur
        setIsTypeaheadInputFocus(false);

        if (shouldDestinationFieldAutoProgressOnSearchSelected(media, form, destination, changedValues)) {
            setTimeout(function delayedDatesOpen() {
                setDatesIsOpen(true);
            }, 300);
        }

        onSearchSelected(...args);
    }

    const result = {
        className,
        appPrefix,
        baseUrl,
        filterType,
        googlePlaceApiKey,
        googlePlaceApiUrl,
        hideSelectedFilter,
        highlightMode,
        icon,
        id,
        isMobile,
        isTypeaheadInputFocus,
        locale,
        matchMode,
        messages,
        minLength,
        onBlur: handleBlur,
        onFocus: handleFocus,
        onMount: handleMount,
        onSearchSelected: handleSearchSelected,
        popoverDesktopMaxWidth,
        popoverDesktopMinMarginRight,
        popoverDesktopMinWidth,
        popoverDesktopWidth,
        popoverMaxDisplayCount,
        popoverMaxDisplayCountMobile,
        recentSearchesDisplayMax,
        remotePath,
        retainUserInput,
        search,
        showInlineSuggestion,
        showRecentSearches,
        showResultIcons,
        showResultLabels,
        showSpinner,
        showTrendingSuggestions,
        site,
        truncateResultTerms,
        usePopoverAnimation,
        useResultsAnimation
    };

    useDebugValue(result);

    return result;
}

export {useComponent};
